// mapbox
import mapboxgl from 'mapbox-gl';

// turf
import * as turf from '@turf/turf';

// vue
import { ref } from '@vue/composition-api';
import { GeoRecord } from '../api/models';

export const useGeoRecordModify = () => {
    const getModifiedGeometry = (
        draw: mapboxgl.Draw,
        geoRecord: GeoRecord,
    ): GeoRecord => {
        const drawGeoRecord = draw.get('test')
            ? draw.get('test')
            : draw.get(geoRecord.id);

        if (!drawGeoRecord) return geoRecord;

        geoRecord.rotation = drawGeoRecord.properties.rotation;
        geoRecord.geometry = drawGeoRecord.geometry;

        return geoRecord;
    };

    const updateModifiedGeoRecord = (
        draw: mapboxgl.Draw,
        geoRecord: any,
        categories: any,
    ): GeoRecord => {
        let geoRecordCategory = categories.filter(
            (category) => category.id === geoRecord.geoRecordCategoryId,
        );

        if (geoRecordCategory.length > 0) {
            geoRecord.color = geoRecordCategory[0].color;
        }

        return getModifiedGeometry(draw, geoRecord);
    };

    const drawMutipleModify = (draw: mapboxgl.Draw, geoRecords: any): void => {
        geoRecords.forEach((geoRecord: any) => {
            draw.add({
                id: geoRecord.id,
                type: 'Feature',
                geometry: geoRecord.geometry,
                properties: {
                    rotation: geoRecord.rotattion,
                },
            });
        });
    };

    const changeDateFormat = (geoRecord: any) => {
        if (geoRecord.startDate !== null) {
            geoRecord.startDate = new Date(geoRecord.startDate);
        }
        if (geoRecord.dueDate !== null) {
            geoRecord.dueDate = new Date(geoRecord.dueDate);
        }
        if (geoRecord.endDate !== null) {
            geoRecord.endDate = new Date(geoRecord.endDate);
        }
        return geoRecord;
    };

    const updateRotationDegree = (
        draw: mapboxgl.Draw,
        rotationAngle: number,
    ) => {
        // refs
        const poly = ref();
        const centroid = ref();

        const geometry = draw.get('test').geometry;

        switch (geometry.type) {
            case 'Polygon':
                poly.value = turf.polygon(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            case 'LineString':
                poly.value = turf.lineString(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            case 'MultiPolygon':
                poly.value = turf.multiPolygon(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            case 'MultiLineString':
                poly.value = turf.multiLineString(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            case 'MultiPoint':
                poly.value = turf.multiPoint(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            case 'Point':
                poly.value = turf.point(geometry.coordinates);
                centroid.value = turf.centroid(poly.value);
                break;
            default:
                console.error('Unsupported geometry type:', geometry.type);
                return;
        }

        let options = {
            pivot: centroid.value?.geometry?.coordinates,
            mutate: false,
        };
        let oldAngle = draw.get('test').properties.rotation || 0;
        let isRectangle = draw.get('test').properties.isRectangle;
        let vertexLabelState = draw.get('test').properties.vertexLabelState;
        let smoothCount = draw.get('test').properties.smoothCount;
        let newAngle = rotationAngle - oldAngle;

        let rotatedPoly;

        if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
            rotatedPoly = turf.transformRotate(poly.value, newAngle, options);
        } else {
            rotatedPoly = turf.transformRotate(poly.value, newAngle, options);
        }

        draw.delete('test');
        draw.add({
            id: 'test',
            type: 'Feature',
            properties: {
                rotation: oldAngle + newAngle,
                isRectangle: isRectangle,
                vertexLabelState: vertexLabelState,
                smoothCount: smoothCount,
            },
            geometry: rotatedPoly.geometry,
        });
    };

    return {
        updateModifiedGeoRecord,
        drawMutipleModify,
        changeDateFormat,
        updateRotationDegree,
    };
};
